import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { database } from '../../firebase';
import { ref, get, set, push, update, remove } from 'firebase/database';
import Swal from 'sweetalert2';
import {
  ShoppingCartIcon, HeartIcon, XMarkIcon,
  ChevronLeftIcon, ChevronRightIcon, MagnifyingGlassIcon,
  StarIcon, ArrowPathIcon, TagIcon, SparklesIcon,
  FolderIcon, ChevronDownIcon, CheckIcon, ArrowRightIcon,
  FireIcon, BoltIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon, HeartIcon as HeartSolidIcon, CheckCircleIcon } from '@heroicons/react/24/solid';

const WHATSAPP_NUMBER = '254748798287';

// ─── SWEETALERT — always above everything ────────────────────────────────────
if (typeof document !== 'undefined' && !document.getElementById('swal-global-z')) {
  const s = document.createElement('style');
  s.id = 'swal-global-z';
  s.textContent = `
    .swal2-container { z-index: 999999 !important; }
    .swal2-backdrop-show { z-index: 999998 !important; }
    .swal2-on-top { z-index: 999999 !important; }
  `;
  document.head.appendChild(s);
}

const toast = (icon, title, text = '') =>
  Swal.fire({ icon, title, text, timer: 2500, toast: true, position: 'top-end', showConfirmButton: false, customClass: { container: 'swal2-on-top' } });

// ─── LOCAL STORAGE HELPERS ───────────────────────────────────────────────────
const LS = {
  get:    (key, fb = null) => { try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fb; } catch { return fb; } },
  set:    (key, val)       => { try { localStorage.setItem(key, JSON.stringify(val)); } catch {} },
  remove: (key)            => { try { localStorage.removeItem(key); } catch {} },
};

// ─── WHATSAPP ICON ───────────────────────────────────────────────────────────
const WhatsAppIcon = ({ className = 'h-4 w-4' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// ─── STARS ───────────────────────────────────────────────────────────────────
const Stars = ({ rating, size = 'sm' }) => {
  const sz = size === 'sm' ? 'h-3 w-3' : 'h-4 w-4';
  return (
    <div className="flex items-center gap-0.5">
      {[1,2,3,4,5].map(s => (
        <StarIcon key={s} className={sz}
          style={{ fill: s <= Math.round(rating) ? '#f59e0b' : 'transparent', color: s <= Math.round(rating) ? '#f59e0b' : '#d1d5db' }} />
      ))}
    </div>
  );
};

// ─── OFFER ACTIVE CHECK ──────────────────────────────────────────────────────
const isOfferActive = (offer) => {
  if (!offer || !offer.isActive) return false;
  const now = Date.now();
  if (!offer.startDate && !offer.endDate) return true;
  if (offer.startDate && offer.endDate)   return now >= offer.startDate && now <= offer.endDate;
  if (offer.startDate && !offer.endDate)  return now >= offer.startDate;
  if (!offer.startDate && offer.endDate)  return now <= offer.endDate;
  return true;
};

// ─── PRICE RESOLVER ─────────────────────────────────────────────────────────
const resolvePrice = (product, isMember) => {
  const regular     = product.price;
  const offerOn     = isOfferActive(product.offer);
  const offerPrice  = product.offer?.price;
  const memberPrice = product.memberPrice;
  if (offerOn && offerPrice && offerPrice < regular) {
    const pct = Math.round(((regular - offerPrice) / regular) * 100);
    return { displayPrice: offerPrice, originalPrice: regular, label: `${pct}% OFF`, labelColor: '#dc2626', hasDiscount: true, isOffer: true, isMemberDeal: false };
  }
  if (isMember && memberPrice && memberPrice < regular) {
    const pct = Math.round(((regular - memberPrice) / regular) * 100);
    return { displayPrice: memberPrice, originalPrice: regular, label: 'Member', labelColor: '#800020', hasDiscount: true, isOffer: false, isMemberDeal: true };
  }
  return { displayPrice: regular, originalPrice: null, label: null, labelColor: null, hasDiscount: false, isOffer: false, isMemberDeal: false };
};

// ─── PRICE DISPLAY ───────────────────────────────────────────────────────────
const PriceDisplay = ({ product, isMember, size = 'sm' }) => {
  const { displayPrice, originalPrice, label, labelColor, hasDiscount } = resolvePrice(product, isMember);
  const mainCls = size === 'lg' ? 'text-3xl font-extrabold' : 'text-xs font-bold';
  const subCls  = size === 'lg' ? 'text-sm' : 'text-[10px]';
  return (
    <div>
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className={mainCls} style={{ color: '#800020' }}>KES {displayPrice?.toLocaleString()}</span>
        {label && (
          <span className="text-[10px] font-bold text-white px-1.5 py-0.5 rounded-md" style={{ background: labelColor }}>{label}</span>
        )}
      </div>
      {hasDiscount && originalPrice && (
        <p className={`${subCls} text-gray-400 line-through`}>KES {originalPrice?.toLocaleString()}</p>
      )}
    </div>
  );
};

// ─── CATEGORY DROPDOWN ───────────────────────────────────────────────────────
const CategoryDropdown = ({ categories, selectedCategory, onSelect, productCounts }) => {
  const [open, setOpen]                 = useState(false);
  const [expandedMain, setExpandedMain] = useState(null);
  const [expandedSub,  setExpandedSub]  = useState(null);
  const dropRef = useRef();

  useEffect(() => {
    const h = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const mainCats = categories.filter(c => c.level === 0 && !c.parentId);
  const getLabel = () => {
    if (!selectedCategory || selectedCategory === 'all') return 'All Categories';
    return categories.find(c => c.id === selectedCategory)?.name || 'All Categories';
  };
  const handleSelect = (id) => { onSelect(id); setOpen(false); };

  return (
    <div ref={dropRef} className="relative">
      <button onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 w-full px-4 py-2.5 bg-white border-2 rounded-xl text-sm font-medium text-gray-700 transition-all focus:outline-none"
        style={{ borderColor: open ? '#800020' : '#e5e7eb' }}>
        <TagIcon className="h-4 w-4 flex-shrink-0" style={{ color: '#800020' }} />
        <span className="flex-1 text-left truncate">{getLabel()}</span>
        <ChevronDownIcon className={`h-4 w-4 text-gray-400 transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: -8, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -8, scale: 0.97 }} transition={{ duration: 0.15 }}
            className="absolute top-full left-0 mt-1.5 w-72 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden" style={{ zIndex: 9999 }}>
            <div className="max-h-80 overflow-y-auto">
              <button onClick={() => handleSelect('all')}
                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium transition-colors hover:bg-gray-50"
                style={{ color: (!selectedCategory || selectedCategory === 'all') ? '#800020' : '#374151' }}>
                <SparklesIcon className="h-4 w-4 flex-shrink-0" />
                <span className="flex-1 text-left">All Categories</span>
                {(!selectedCategory || selectedCategory === 'all') && <CheckIcon className="h-4 w-4" style={{ color: '#800020' }} />}
              </button>
              {mainCats.map(cat => {
                const subs  = categories.filter(c => c.parentId === cat.id && c.level === 1);
                const isSel = selectedCategory === cat.id;
                const isExp = expandedMain === cat.id;
                return (
                  <div key={cat.id}>
                    <div className="flex items-center" style={{ background: isSel ? '#fef2f2' : undefined }}>
                      <button onClick={() => handleSelect(cat.id)}
                        className="flex-1 flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium transition-colors hover:bg-gray-50"
                        style={{ color: isSel ? '#800020' : '#374151' }}>
                        {cat.imageUrl ? <img src={cat.imageUrl} alt="" className="h-5 w-5 rounded-md object-cover flex-shrink-0" /> : <FolderIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />}
                        <span className="flex-1 text-left">{cat.name}</span>
                        <span className="text-xs text-gray-400 mr-1">{productCounts[cat.id] || 0}</span>
                        {isSel && <CheckIcon className="h-4 w-4 flex-shrink-0" style={{ color: '#800020' }} />}
                      </button>
                      {subs.length > 0 && (
                        <button onClick={() => setExpandedMain(isExp ? null : cat.id)} className="px-3 py-2.5 text-gray-400 hover:text-gray-600 transition-colors">
                          <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform ${isExp ? 'rotate-180' : ''}`} />
                        </button>
                      )}
                    </div>
                    {isExp && subs.map(sub => {
                      const subSubs  = categories.filter(c => c.parentId === sub.id && c.level === 2);
                      const isSubSel = selectedCategory === sub.id;
                      const isSubExp = expandedSub === sub.id;
                      return (
                        <div key={sub.id}>
                          <div className="flex items-center" style={{ background: isSubSel ? '#fef2f2' : undefined }}>
                            <button onClick={() => handleSelect(sub.id)}
                              className="flex-1 flex items-center gap-2 pl-9 pr-3 py-2 text-sm transition-colors hover:bg-gray-50"
                              style={{ color: isSubSel ? '#800020' : '#6b7280' }}>
                              <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: isSubSel ? '#800020' : '#d1d5db' }} />
                              <span className="flex-1 text-left">{sub.name}</span>
                              <span className="text-xs text-gray-400 mr-1">{productCounts[sub.id] || 0}</span>
                              {isSubSel && <CheckIcon className="h-3.5 w-3.5 flex-shrink-0" style={{ color: '#800020' }} />}
                            </button>
                            {subSubs.length > 0 && (
                              <button onClick={() => setExpandedSub(isSubExp ? null : sub.id)} className="px-2 py-2 text-gray-400 hover:text-gray-600">
                                <ChevronDownIcon className={`h-3 w-3 transition-transform ${isSubExp ? 'rotate-180' : ''}`} />
                              </button>
                            )}
                          </div>
                          {isSubExp && subSubs.map(ss => (
                            <button key={ss.id} onClick={() => handleSelect(ss.id)}
                              className="w-full flex items-center gap-2 pl-14 pr-3 py-1.5 text-xs transition-colors hover:bg-gray-50"
                              style={{ color: selectedCategory === ss.id ? '#800020' : '#9ca3af', background: selectedCategory === ss.id ? '#fef2f2' : undefined }}>
                              <span className="w-1 h-1 rounded-full bg-gray-200 flex-shrink-0" />
                              <span className="flex-1 text-left">{ss.name}</span>
                              <span className="text-xs text-gray-300 mr-1">{productCounts[ss.id] || 0}</span>
                              {selectedCategory === ss.id && <CheckIcon className="h-3 w-3 flex-shrink-0" style={{ color: '#800020' }} />}
                            </button>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── PRODUCT CARD ────────────────────────────────────────────────────────────
const ProductCard = ({ product, isInCart, isLiked, onAddToCart, onToggleLike, onOpenModal, onWhatsApp, isMember }) => {
  const [viewHov, setViewHov] = useState(false);
  const offerActive = isOfferActive(product.offer);
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
      className="group bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden relative flex flex-col"
      style={{ transition: 'box-shadow 0.2s ease, transform 0.2s ease' }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 24px rgba(128,0,32,0.13)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = ''; e.currentTarget.style.transform = ''; }}
    >
      <button onClick={e => { e.stopPropagation(); onToggleLike(product); }}
        className="absolute top-2 left-2 z-10 w-7 h-7 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-sm"
        style={{ transition: 'transform 0.15s' }}
        onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.2)'}
        onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
        title={isLiked ? 'Remove from favorites' : 'Add to favorites'}>
        {isLiked
          ? <HeartSolidIcon className="h-3.5 w-3.5" style={{ color: '#ef4444' }} />
          : <HeartIcon className="h-3.5 w-3.5 text-gray-400" />}
      </button>
      <button onClick={e => { e.stopPropagation(); onWhatsApp(product); }}
        className="absolute top-2 right-2 z-10 w-7 h-7 rounded-full flex items-center justify-center shadow-sm"
        style={{ background: '#25D366', color: '#fff', transition: 'transform 0.15s, background 0.15s' }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.15)'; e.currentTarget.style.background = '#1da851'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.background = '#25D366'; }}
        title="Order on WhatsApp">
        <WhatsAppIcon className="h-3.5 w-3.5" />
      </button>
      {offerActive && (
        <div className="absolute top-9 right-2 z-10">
          <span className="text-[9px] font-extrabold text-white px-1.5 py-0.5 rounded shadow-md" style={{ background: '#dc2626' }}>SALE</span>
        </div>
      )}
      <div className="relative bg-gray-50 cursor-pointer flex-shrink-0" style={{ height: 130 }} onClick={() => onOpenModal(product)}>
        {product.images?.[0]
          ? <img src={product.images[0]} alt={product.name} className="w-full h-full object-contain p-2" />
          : <div className="w-full h-full flex items-center justify-center"><TagIcon className="h-10 w-10 text-gray-200" /></div>}
        {product.averageRating > 0 && (
          <div className="absolute bottom-1.5 left-1.5 flex items-center gap-1 bg-black/60 backdrop-blur-sm rounded-lg px-1.5 py-0.5">
            <StarSolidIcon className="h-2.5 w-2.5 text-yellow-400" />
            <span className="text-white text-[10px] font-bold">{product.averageRating.toFixed(1)}</span>
            <span className="text-white/60 text-[9px]">({product.totalRatings})</span>
          </div>
        )}
        {product.totalLikes > 0 && (
          <div className="absolute bottom-1.5 right-1.5 flex items-center gap-0.5 bg-black/60 backdrop-blur-sm rounded-lg px-1.5 py-0.5">
            <HeartSolidIcon className="h-2.5 w-2.5 text-red-400" />
            <span className="text-white text-[10px] font-bold">{product.totalLikes}</span>
          </div>
        )}
      </div>
      <div className="p-2.5 flex flex-col flex-1">
        <p className="text-xs font-semibold text-gray-800 leading-tight line-clamp-2" style={{ minHeight: '2rem' }}>{product.name}</p>
        {product.brand && <p className="text-[10px] text-gray-400 mt-0.5 truncate">{product.brand}</p>}
        <div className="mt-1.5 mb-2.5"><PriceDisplay product={product} isMember={isMember} size="sm" /></div>
        <div className="flex flex-col gap-1.5 mt-auto">
          <button onClick={() => onOpenModal(product)}
            onMouseEnter={() => setViewHov(true)} onMouseLeave={() => setViewHov(false)}
            className="w-full py-1.5 rounded-lg text-[11px] font-semibold text-white"
            style={{
              background: viewHov ? 'linear-gradient(135deg,#600018,#900000)' : 'linear-gradient(135deg,#800020,#b30000)',
              transform: viewHov ? 'scale(1.02)' : 'scale(1)',
              boxShadow: viewHov ? '0 4px 12px rgba(128,0,32,0.28)' : 'none',
              transition: 'all 0.15s ease',
            }}>View Details</button>
          <button
            onClick={e => { e.stopPropagation(); if (!isInCart) onAddToCart(product); }}
            disabled={isInCart}
            className="w-full py-1.5 rounded-lg text-[11px] font-semibold text-white flex items-center justify-center gap-1"
            style={{ background: isInCart ? '#16a34a' : '#374151', cursor: isInCart ? 'not-allowed' : 'pointer', opacity: isInCart ? 0.9 : 1, transition: 'all 0.15s ease' }}
            onMouseEnter={e => { if (!isInCart) e.currentTarget.style.background = '#2d3748'; }}
            onMouseLeave={e => { if (!isInCart) e.currentTarget.style.background = '#374151'; }}>
            {isInCart ? <><CheckCircleIcon className="h-3 w-3 flex-shrink-0" /> In Cart</> : 'Add to Cart'}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN SHOP
// ═══════════════════════════════════════════════════════════════════════════════
const Shop = () => {
  const { currentUser, userData } = useAuth();
  const navigate = useNavigate();
  const isMember = !!(currentUser && userData?.membership?.status === 'active');

  const [allProducts,       setAllProducts]      = useState([]);
  const [categories,        setCategories]        = useState([]);
  const [filteredProducts,  setFilteredProducts]  = useState([]);
  const [loading,           setLoading]           = useState(true);
  const [selectedCategory,  setSelectedCategory]  = useState('all');
  const [searchQuery,       setSearchQuery]       = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions,   setShowSuggestions]   = useState(false);
  const [cart,              setCart]              = useState([]);
  const [likedProducts,     setLikedProducts]     = useState([]);
  const [selectedProduct,   setSelectedProduct]   = useState(null);
  const [userRating,        setUserRating]        = useState(0);
  const [hoverRating,       setHoverRating]       = useState(0);
  const [ratingComment,     setRatingComment]     = useState('');
  const [heroIndex,         setHeroIndex]         = useState(0);
  const [heroCategories,    setHeroCategories]    = useState([]);
  const [userRatings,       setUserRatings]       = useState({});
  const [selectedImageIdx,  setSelectedImageIdx]  = useState(0);
  const heroTimerRef = useRef(null);
  const searchRef    = useRef();

  useEffect(() => { fetchProducts(); fetchCategories(); }, []);

  useEffect(() => {
    if (currentUser) { loadFromFirebase(); }
    else { setCart(LS.get('shop_cart', [])); setLikedProducts(LS.get('liked_products', [])); setUserRatings(LS.get('user_ratings', {})); }
  }, [currentUser]);

  useEffect(() => { applyFilters(); }, [selectedCategory, searchQuery, allProducts, categories]);

  useEffect(() => {
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      setSearchSuggestions(allProducts.filter(p =>
        p.name?.toLowerCase().includes(q) || p.brand?.toLowerCase().includes(q) || p.description?.toLowerCase().includes(q)
      ).slice(0, 6));
      setShowSuggestions(true);
    } else { setSearchSuggestions([]); setShowSuggestions(false); }
  }, [searchQuery, allProducts]);

  useEffect(() => {
    if (heroCategories.length > 1) {
      clearInterval(heroTimerRef.current);
      heroTimerRef.current = setInterval(() => setHeroIndex(p => (p + 1) % heroCategories.length), 5000);
    }
    return () => clearInterval(heroTimerRef.current);
  }, [heroCategories]);

  useEffect(() => {
    const h = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) setShowSuggestions(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const loadFromFirebase = async () => {
    try {
      const cartRef  = ref(database, `users/${currentUser.uid}/cart`);
      const cartSnap = await get(cartRef);
      const fbCart   = cartSnap.exists() ? (Array.isArray(cartSnap.val()) ? cartSnap.val() : Object.values(cartSnap.val())) : [];
      const lcCart   = LS.get('shop_cart', []);
      let mergedCart = [...fbCart];
      lcCart.forEach(li => {
        const idx = mergedCart.findIndex(i => i.id === li.id);
        if (idx >= 0) mergedCart[idx].quantity += li.quantity; else mergedCart.push(li);
      });
      setCart(mergedCart);
      if (lcCart.length > 0) { await set(cartRef, mergedCart); LS.remove('shop_cart'); }

      const likesRef  = ref(database, `users/${currentUser.uid}/likedProducts`);
      const likesSnap = await get(likesRef);
      const fbLikes   = likesSnap.exists() ? (Array.isArray(likesSnap.val()) ? likesSnap.val() : Object.values(likesSnap.val())) : [];
      const lcLikes   = LS.get('liked_products', []);
      const mergedLikes = [...new Set([...fbLikes, ...lcLikes])];
      setLikedProducts(mergedLikes);
      if (lcLikes.length > 0) { await set(likesRef, mergedLikes); LS.remove('liked_products'); }
      else { await set(likesRef, mergedLikes); }

      const pendingLikes = LS.get('pending_likes', []);
      for (const pl of pendingLikes) { try { await _writeLikeEngagement(pl.product, pl.liked); } catch {} }
      if (pendingLikes.length > 0) LS.remove('pending_likes');

      const lcWishlist = LS.get('wishlist', {});
      if (Object.keys(lcWishlist).length > 0) {
        for (const [productId, entry] of Object.entries(lcWishlist)) {
          try { if (entry.isWishlist && mergedLikes.includes(productId)) await set(ref(database, `users/${currentUser.uid}/wishlist/${productId}`), { ...entry, isWishlist: true }); } catch {}
        }
        LS.remove('wishlist');
      }

      const ratingsRef  = ref(database, `users/${currentUser.uid}/productRatings`);
      const ratingsSnap = await get(ratingsRef);
      const fbRatings   = ratingsSnap.exists() ? ratingsSnap.val() : {};
      const lcRatings   = LS.get('user_ratings', {});
      const mergedRatings = { ...fbRatings, ...lcRatings };
      setUserRatings(mergedRatings);
      if (Object.keys(lcRatings).length > 0) { await set(ratingsRef, mergedRatings); LS.remove('user_ratings'); }
    } catch (e) { console.error('Firebase load error:', e); }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const snap = await get(ref(database, 'products'));
      if (snap.exists()) {
        setAllProducts(Object.entries(snap.val()).map(([id, d]) => ({
          id, ...d,
          totalSales: d.totalSales || 0, totalViews: d.totalViews || 0,
          totalLikes: d.totalLikes || 0, averageRating: d.averageRating || 0, totalRatings: d.totalRatings || 0,
        })));
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchCategories = async () => {
    try {
      const snap = await get(ref(database, 'categories'));
      if (snap.exists()) setCategories(Object.entries(snap.val()).map(([id, d]) => ({ id, ...d })));
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    if (categories.length === 0) return;
    const mainCats = categories.filter(c => c.level === 0 && !c.parentId);
    const withStats = mainCats.map(cat => {
      const prods = allProducts.filter(p => p.categoryId === cat.id);
      return { ...cat, interactions: prods.reduce((s, p) => s + (p.totalViews || 0) + (p.totalSales || 0) + (p.totalLikes || 0) * 2, 0), productCount: prods.length };
    });
    setHeroCategories([...withStats].sort((a, b) => b.interactions - a.interactions).slice(0, 10));
    setHeroIndex(0);
  }, [categories, allProducts]);

  const applyFilters = () => {
    let result = [...allProducts];
    if (selectedCategory && selectedCategory !== 'all') {
      const cat = categories.find(c => c.id === selectedCategory);
      if (cat) {
        const ids = new Set([selectedCategory]);
        if (cat.level === 0) { categories.filter(c => c.parentId === selectedCategory).forEach(s => { ids.add(s.id); categories.filter(c => c.parentId === s.id).forEach(ss => ids.add(ss.id)); }); }
        if (cat.level === 1) categories.filter(c => c.parentId === selectedCategory).forEach(ss => ids.add(ss.id));
        result = result.filter(p => ids.has(p.categoryId) || ids.has(p.subCategoryId) || ids.has(p.subSubCategoryId));
      }
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(p => p.name?.toLowerCase().includes(q) || p.brand?.toLowerCase().includes(q) || p.description?.toLowerCase().includes(q));
    }
    setFilteredProducts(result);
  };

  const productCounts = useMemo(() => {
    const c = {};
    allProducts.forEach(p => { [p.categoryId, p.subCategoryId, p.subSubCategoryId].filter(Boolean).forEach(id => { c[id] = (c[id] || 0) + 1; }); });
    return c;
  }, [allProducts]);

  const trackView = async (productId) => {
    const lv = LS.get('local_views', {});
    lv[productId] = (lv[productId] || 0) + 1;
    LS.set('local_views', lv);
    try {
      const pr = ref(database, `products/${productId}`);
      const snap = await get(pr);
      if (snap.exists()) await update(pr, { totalViews: (snap.val().totalViews || 0) + 1 });
    } catch (e) { console.error('View tracking:', e); }
  };

  const _writeLikeEngagement = async (product, nowLiked) => {
    const productRef   = ref(database, `products/${product.id}`);
    const snap         = await get(productRef);
    if (!snap.exists()) return;
    const currentLikes = snap.val().totalLikes || 0;
    const newLikes     = nowLiked ? currentLikes + 1 : Math.max(0, currentLikes - 1);
    await update(productRef, { totalLikes: newLikes });
    const actorKey = currentUser?.uid || `guest_${Date.now()}`;
    const engRef   = ref(database, `productEngagement/${product.id}/likes/${actorKey}`);
    if (nowLiked) {
      await set(engRef, { liked: true, isWishlist: true, userId: currentUser?.uid || null, userName: userData?.personalInfo?.name || currentUser?.email || 'Guest', categoryId: product.categoryId || null, productName: product.name, date: Date.now() });
    } else { await remove(engRef); }
    setAllProducts(prev => prev.map(p => p.id === product.id ? { ...p, totalLikes: newLikes } : p));
  };

  const toggleLike = async (product) => {
    const wasLiked = likedProducts.includes(product.id);
    const nowLiked = !wasLiked;
    const updated  = nowLiked ? [...likedProducts, product.id] : likedProducts.filter(id => id !== product.id);
    setLikedProducts(updated);
    if (currentUser) {
      try {
        await set(ref(database, `users/${currentUser.uid}/likedProducts`), updated);
        const wishlistRef = ref(database, `users/${currentUser.uid}/wishlist/${product.id}`);
        if (nowLiked) { await set(wishlistRef, { isWishlist: true, productId: product.id, productName: product.name, categoryId: product.categoryId || null, image: product.images?.[0] || null, addedAt: Date.now() }); }
        else { await remove(wishlistRef); }
        await _writeLikeEngagement(product, nowLiked);
      } catch (e) { console.error('Like error:', e); }
    } else {
      LS.set('liked_products', updated);
      const lsWishlist = LS.get('wishlist', {});
      if (nowLiked) { lsWishlist[product.id] = { isWishlist: true, productId: product.id, productName: product.name, categoryId: product.categoryId || null, image: product.images?.[0] || null, addedAt: Date.now() }; }
      else { delete lsWishlist[product.id]; }
      LS.set('wishlist', lsWishlist);
      const pending  = LS.get('pending_likes', []);
      const filtered = pending.filter(pl => pl.product.id !== product.id);
      filtered.push({ product, liked: nowLiked });
      LS.set('pending_likes', filtered);
      try { await _writeLikeEngagement(product, nowLiked); } catch {}
    }
    toast('success', nowLiked ? 'Added to favorites ♥' : 'Removed from favorites');
  };

  const addToCart = async (product) => {
    const { displayPrice } = resolvePrice(product, isMember);
    const item = { id: product.id, name: product.name, price: displayPrice, originalPrice: product.price, memberPrice: product.memberPrice || null, offerPrice: product.offer?.price || null, quantity: 1, image: product.images?.[0] || null, variantType: product.variantType };
    if (currentUser) {
      try {
        const cartRef = ref(database, `users/${currentUser.uid}/cart`);
        const snap    = await get(cartRef);
        const cur     = snap.exists() ? (Array.isArray(snap.val()) ? snap.val() : Object.values(snap.val())) : [];
        const idx     = cur.findIndex(i => i.id === item.id);
        if (idx >= 0) cur[idx].quantity += 1; else cur.push(item);
        await set(cartRef, cur);
        setCart([...cur]);
      } catch { toast('error', 'Error', 'Could not add to cart'); return; }
    } else {
      const upd = [...cart];
      const idx = upd.findIndex(i => i.id === item.id);
      if (idx >= 0) upd[idx].quantity += 1; else upd.push(item);
      setCart(upd);
      LS.set('shop_cart', upd);
    }
    toast('success', 'Added to cart!', product.name);
  };

  const openModal = async (product) => {
    setSelectedProduct(product); setSelectedImageIdx(0); setHoverRating(0);
    if (currentUser) {
      try {
        const snap = await get(ref(database, `users/${currentUser.uid}/productRatings/${product.id}`));
        if (snap.exists()) { setUserRating(snap.val().rating); setRatingComment(snap.val().comment || ''); }
        else { setUserRating(0); setRatingComment(''); }
      } catch { setUserRating(0); setRatingComment(''); }
    } else {
      const saved = userRatings[product.id];
      if (saved) { setUserRating(saved.rating); setRatingComment(saved.comment || ''); }
      else { setUserRating(0); setRatingComment(''); }
    }
    await trackView(product.id);
  };

  const submitRating = async () => {
    if (!userRating || !selectedProduct) return toast('warning', 'Please select a star rating');
    try {
      const pr   = ref(database, `products/${selectedProduct.id}`);
      const snap = await get(pr);
      const prod = snap.val();
      let existing = 0;
      if (currentUser) { const rs = await get(ref(database, `users/${currentUser.uid}/productRatings/${selectedProduct.id}`)); if (rs.exists()) existing = rs.val().rating; }
      else { existing = userRatings[selectedProduct.id]?.rating || 0; }
      let total = prod.totalRatings || 0, avg = prod.averageRating || 0;
      if (existing > 0) { avg = ((avg * total) - existing + userRating) / total; }
      else { total++; avg = ((avg * (total - 1)) + userRating) / total; }
      await update(pr, { averageRating: avg, totalRatings: total });
      const rd = { rating: userRating, comment: ratingComment, date: Date.now(), userName: userData?.personalInfo?.name || currentUser?.email || 'Guest' };
      if (currentUser) { await set(ref(database, `users/${currentUser.uid}/productRatings/${selectedProduct.id}`), rd); }
      else { const nr = { ...userRatings, [selectedProduct.id]: rd }; setUserRatings(nr); LS.set('user_ratings', nr); }
      toast('success', 'Rating submitted! Thank you ★');
      setSelectedProduct(p => p ? { ...p, averageRating: avg, totalRatings: total } : null);
      fetchProducts();
    } catch (e) { console.error(e); toast('error', 'Could not submit rating'); }
  };

  // ─── GENERATE ORDER NUMBER ────────────────────────────────────────────────
  const generateOrderNumber = async () => {
    try {
      const counterRef = ref(database, 'system/counters/orderNumber');
      const snap = await get(counterRef);
      const next = (snap.exists() ? snap.val() : 1000) + 1;
      await set(counterRef, next);
      return `ORD-${next}`;
    } catch {
      return `ORD-${Date.now()}`;
    }
  };

  // ─── BUILD WHATSAPP MESSAGE ───────────────────────────────────────────────
  const buildWhatsAppMessage = ({ orderNumber, product, displayPrice, label }) => {
    const priceNote = label ? ` (${label})` : '';
    return (
      `🛒 *New Order: ${orderNumber}*%0A` +
      `══════════════════════%0A` +
      `📦 *Item:*%0A` +
      `  • ${product.name} x1 — KES ${displayPrice?.toLocaleString()}${priceNote}%0A` +
      (product.brand ? `  Brand: ${product.brand}%0A` : '') +
      `%0A` +
      `💰 *Total: KES ${displayPrice?.toLocaleString()}*%0A` +
      `%0A` +
      `_Payment: To be arranged on delivery_`
    );
  };

  // ─── OPEN WHATSAPP ────────────────────────────────────────────────────────
  // Saves to `orders` collection (same as Checkout).
  // Logged-in → real uid, skip Swal.
  // Guest     → show maroon Swal, save with userId: 'guest'.
  const openWhatsApp = async (product) => {
    const { displayPrice, label } = resolvePrice(product, isMember);

    const executeWhatsApp = async (userId) => {
      const orderNumber = await generateOrderNumber();

      // Build order — only include fields we actually have.
      // deliveryInfo and full userInfo are empty for card/modal orders.
      const orderRecord = {
        orderNumber,
        userId,
        userInfo: {
          name:    userData?.personalInfo?.name  || '',
          email:   currentUser?.email            || '',
          phone:   userData?.personalInfo?.phone || '',
          company: '',
        },
        deliveryInfo: {
          address:     '',
          area:        '',
          city:        '',
          landmark:    '',
          notes:       '',
          contactPref: '',
        },
        items: [{
          productId:     product.id,
          name:          product.name,
          quantity:      1,
          price:         displayPrice,
          originalPrice: product.price,
          subtotal:      displayPrice,
          image:         product.images?.[0] || '',
          variant:       { color: '', size: '' },
        }],
        subtotal:         displayPrice,
        savings:          product.price - displayPrice > 0 ? product.price - displayPrice : 0,
        tax:              0,
        total:            displayPrice,
        paymentMethod:    'whatsapp_cod',
        paymentReference: '',
        paymentStatus:    'pending',
        orderStatus:      'pending',
        orderSource:      'shop_card',
        createdAt:        Date.now(),
      };

      // Save to `orders` — same collection as Checkout
      try {
        const orderRef = push(ref(database, 'orders'));
        await set(orderRef, { ...orderRecord, id: orderRef.key });

        // Logged-in: also save under recentOrders
        if (currentUser) {
          await set(ref(database, `users/${currentUser.uid}/recentOrders/${orderRef.key}`), {
            orderId:     orderRef.key,
            orderNumber,
            total:       displayPrice,
            status:      'pending',
            orderSource: 'shop_card',
            date:        Date.now(),
          });
        }
      } catch (e) {
        console.error('Order save error:', e);
      }

      await trackView(product.id);
      const msg = buildWhatsAppMessage({ orderNumber, product, displayPrice, label });
      window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${msg}`, '_blank');
    };

    // Already logged in → go straight
    if (currentUser) {
      await executeWhatsApp(currentUser.uid);
      return;
    }

    // Guest → maroon Swal
    let swalChoice = null;
    await Swal.fire({
      html: `
        <div style="padding: 4px 0 0;">
          <div style="width:54px;height:54px;border-radius:18px;background:linear-gradient(135deg,#800020,#b30000);display:flex;align-items:center;justify-content:center;margin:0 auto 18px;box-shadow:0 8px 24px rgba(128,0,32,0.32);">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
          </div>
          <h2 style="font-size:17px;font-weight:800;color:#111827;margin:0 0 8px;letter-spacing:-0.3px;">Order via WhatsApp</h2>
          <p style="font-size:13px;color:#6b7280;margin:0 0 22px;line-height:1.6;">Login to track your order history and get<br/>updates, or proceed directly to WhatsApp.</p>
          <div style="display:flex;flex-direction:column;gap:10px;">
            <button id="swal-wa-login" style="width:100%;padding:14px 20px;background:linear-gradient(135deg,#800020,#b30000);color:white;border:none;border-radius:14px;font-size:13.5px;font-weight:700;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:9px;box-shadow:0 4px 16px rgba(128,0,32,0.3);">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>
              Login to Track Order
            </button>
            <button id="swal-wa-guest" style="width:100%;padding:14px 20px;background:#f0fdf4;color:#15803d;border:2px solid #86efac;border-radius:14px;font-size:13.5px;font-weight:700;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:9px;">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              Proceed without Login
            </button>
          </div>
          <p style="font-size:11px;color:#9ca3af;margin:16px 0 0;line-height:1.5;">Your order details will still be recorded for our team.</p>
        </div>
      `,
      showConfirmButton: false,
      showCloseButton:   true,
      background:        '#ffffff',
      width:             370,
      padding:           '28px 28px 26px',
      customClass:       { container: 'swal2-on-top', popup: 'swal-wa-popup' },
      didOpen: (popup) => {
        popup.querySelector('#swal-wa-login')?.addEventListener('click',  () => { swalChoice = 'login';  Swal.close(); });
        popup.querySelector('#swal-wa-guest')?.addEventListener('click',  () => { swalChoice = 'guest';  Swal.close(); });
      },
    });

    if (swalChoice === 'login')  { navigate('/login'); return; }
    if (swalChoice === 'guest')  { await executeWhatsApp('guest'); }
  };

  const goToHero = (idx) => {
    setHeroIndex(idx);
    clearInterval(heroTimerRef.current);
    heroTimerRef.current = setInterval(() => setHeroIndex(p => (p + 1) % heroCategories.length), 5000);
  };
  const exploreCatFromHero = (catId) => { setSelectedCategory(catId); document.getElementById('products-section')?.scrollIntoView({ behavior: 'smooth' }); };
  const getActiveCatLabel  = () => !selectedCategory || selectedCategory === 'all' ? null : categories.find(c => c.id === selectedCategory)?.name || null;
  const clearFilters       = () => { setSelectedCategory('all'); setSearchQuery(''); };

  return (
    <div className="min-h-screen bg-gray-50">

      {/* HERO */}
      <div className="relative overflow-hidden bg-gray-900" style={{ height: 420 }}>
        <AnimatePresence mode="wait">
          {heroCategories.length > 0 && (
            <motion.div key={heroIndex} initial={{ opacity: 0, scale: 1.06 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.97 }} transition={{ duration: 0.75 }}
              className="absolute inset-0"
              style={{ backgroundImage: heroCategories[heroIndex]?.imageUrl ? `url(${heroCategories[heroIndex].imageUrl})` : 'linear-gradient(135deg,#1a1a2e 0%,#800020 100%)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
              <div className="absolute inset-0" style={{ background: 'linear-gradient(110deg,rgba(0,0,0,0.82) 0%,rgba(0,0,0,0.45) 55%,transparent 100%)' }} />
            </motion.div>
          )}
        </AnimatePresence>
        <div className="relative h-full flex flex-col justify-center px-8 md:px-16" style={{ maxWidth: 900 }}>
          <AnimatePresence mode="wait">
            {heroCategories.length > 0 && (
              <motion.div key={heroIndex} initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -18 }} transition={{ duration: 0.5 }}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold text-white"
                    style={{ background: heroCategories[heroIndex]?.interactions > 0 ? 'rgba(220,38,38,0.8)' : 'rgba(128,0,32,0.7)' }}>
                    {heroCategories[heroIndex]?.interactions > 0 ? <><FireIcon className="h-3 w-3" /> Trending</> : <><BoltIcon className="h-3 w-3" /> Featured</>}
                  </span>
                  <span className="text-white/40 text-xs">{heroIndex + 1} / {heroCategories.length}</span>
                </div>
                <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-3 leading-tight">{heroCategories[heroIndex]?.name}</h1>
                <div className="flex items-center gap-2 mb-6">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold text-white/90" style={{ background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(6px)' }}>
                    <BoltIcon className="h-4 w-4 text-yellow-400" />{(heroCategories[heroIndex]?.interactions || 0).toLocaleString()} interactions
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold text-white/90" style={{ background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(6px)' }}>
                    <TagIcon className="h-4 w-4 text-blue-300" />{heroCategories[heroIndex]?.productCount || 0} products
                  </span>
                </div>
                <motion.button whileHover={{ scale: 1.04, boxShadow: '0 8px 28px rgba(128,0,32,0.45)' }} whileTap={{ scale: 0.97 }}
                  onClick={() => exploreCatFromHero(heroCategories[heroIndex]?.id)}
                  className="inline-flex items-center gap-2 px-7 py-3 rounded-2xl text-sm font-bold text-white shadow-xl"
                  style={{ background: 'linear-gradient(135deg,#800020,#b30000)' }}>
                  Explore {heroCategories[heroIndex]?.name}<ArrowRightIcon className="h-4 w-4" />
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        {heroCategories.length > 1 && (
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-1.5">
            {heroCategories.map((_, i) => (
              <button key={i} onClick={() => goToHero(i)} className="rounded-full transition-all duration-300"
                style={{ width: i === heroIndex ? 24 : 8, height: 8, background: i === heroIndex ? '#fff' : 'rgba(255,255,255,0.3)' }} />
            ))}
          </div>
        )}
        {heroCategories.length > 1 && (
          <>
            <button onClick={() => goToHero((heroIndex - 1 + heroCategories.length) % heroCategories.length)} className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/30 hover:bg-black/55 text-white flex items-center justify-center backdrop-blur-sm transition-all"><ChevronLeftIcon className="h-5 w-5" /></button>
            <button onClick={() => goToHero((heroIndex + 1) % heroCategories.length)} className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/30 hover:bg-black/55 text-white flex items-center justify-center backdrop-blur-sm transition-all"><ChevronRightIcon className="h-5 w-5" /></button>
          </>
        )}
      </div>

      {/* CONTENT */}
      <div id="products-section" className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-6">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-5">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="md:w-72">
              <CategoryDropdown categories={categories} selectedCategory={selectedCategory} onSelect={setSelectedCategory} productCounts={productCounts} />
            </div>
            <div className="flex-1 relative" ref={searchRef}>
              <MagnifyingGlassIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none z-10" />
              <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onFocus={() => searchQuery && setShowSuggestions(true)}
                placeholder="Search by name, brand or description…"
                className="w-full pl-10 pr-10 py-2.5 bg-white border-2 border-gray-200 rounded-xl text-sm focus:outline-none transition-all"
                style={{ borderColor: searchQuery ? '#800020' : '#e5e7eb' }} />
              {searchQuery && (
                <button onClick={() => { setSearchQuery(''); setShowSuggestions(false); }} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <XMarkIcon className="h-4 w-4" />
                </button>
              )}
              <AnimatePresence>
                {showSuggestions && searchSuggestions.length > 0 && (
                  <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                    className="absolute top-full left-0 right-0 mt-1.5 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden" style={{ zIndex: 9999 }}>
                    {searchSuggestions.map(p => (
                      <button key={p.id} onClick={() => { setSearchQuery(p.name); setShowSuggestions(false); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors text-left">
                        {p.images?.[0] ? <img src={p.images[0]} alt="" className="w-9 h-9 rounded-lg object-cover flex-shrink-0" /> : <div className="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0"><TagIcon className="h-4 w-4 text-gray-300" /></div>}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-800 truncate">{p.name}</p>
                          <p className="text-xs text-gray-400 truncate">{p.brand}</p>
                        </div>
                        <div className="flex-shrink-0 text-right">
                          <p className="text-sm font-bold" style={{ color: '#800020' }}>KES {resolvePrice(p, isMember).displayPrice?.toLocaleString()}</p>
                          {resolvePrice(p, isMember).hasDiscount && <p className="text-[10px] text-gray-400 line-through">KES {p.price?.toLocaleString()}</p>}
                        </div>
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            {(selectedCategory !== 'all' || searchQuery) && (
              <motion.button initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} onClick={clearFilters}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold border-2 transition-all whitespace-nowrap flex-shrink-0"
                style={{ borderColor: '#800020', color: '#800020' }}
                onMouseEnter={e => e.currentTarget.style.background = '#fef2f2'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <XMarkIcon className="h-4 w-4" /> Clear filters
              </motion.button>
            )}
          </div>
          {getActiveCatLabel() && (
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
              <span className="text-xs text-gray-400">Showing:</span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold text-white" style={{ background: '#800020' }}>
                <TagIcon className="h-3 w-3" />{getActiveCatLabel()}
                <button onClick={() => setSelectedCategory('all')} className="ml-0.5 opacity-70 hover:opacity-100"><XMarkIcon className="h-3 w-3" /></button>
              </span>
            </div>
          )}
        </div>

        <p className="text-xs text-gray-400 mb-4 font-medium px-1">
          {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''}
          {getActiveCatLabel() ? ` in "${getActiveCatLabel()}"` : ''}
          {searchQuery ? ` matching "${searchQuery}"` : ''}
        </p>

        {loading ? (
          <div className="flex justify-center items-center py-24"><ArrowPathIcon className="h-8 w-8 animate-spin" style={{ color: '#800020' }} /></div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-24 bg-white rounded-2xl border border-gray-100">
            <div className="w-20 h-20 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4"><ShoppingCartIcon className="h-10 w-10 text-gray-300" /></div>
            <p className="text-gray-500 font-medium">No products found</p>
            <button onClick={clearFilters} className="mt-3 text-sm font-semibold" style={{ color: '#800020' }}>Clear filters</button>
          </div>
        ) : (
          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}>
            <AnimatePresence>
              {filteredProducts.map(product => (
                <ProductCard key={product.id} product={product}
                  isInCart={cart.some(i => i.id === product.id)}
                  isLiked={likedProducts.includes(product.id)}
                  isMember={isMember}
                  onAddToCart={addToCart} onToggleLike={toggleLike}
                  onOpenModal={openModal} onWhatsApp={openWhatsApp} />
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* DETAIL MODAL */}
      <AnimatePresence>
        {selectedProduct && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 flex" style={{ zIndex: 99999, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)' }}
            onClick={() => setSelectedProduct(null)}>
            <motion.div initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 280 }}
              className="ml-auto flex flex-col bg-white shadow-2xl"
              style={{ width: '100%', maxWidth: '100vw', height: '100dvh', zIndex: 100000 }}
              onClick={e => e.stopPropagation()}>
              <div className="flex-shrink-0 flex items-center justify-between px-6 py-4" style={{ background: 'linear-gradient(135deg,#1a1a2e 0%,#800020 100%)' }}>
                <div>
                  <h2 className="text-lg font-bold text-white">Product Details</h2>
                  <p className="text-white/50 text-xs mt-0.5">View, rate & order</p>
                </div>
                <button onClick={() => setSelectedProduct(null)}
                  className="w-9 h-9 flex items-center justify-center rounded-xl text-white transition-all" style={{ background: 'rgba(255,255,255,0.12)' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.22)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}>
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto">
                <div className="max-w-5xl mx-auto px-6 py-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <div className="relative bg-gray-50 rounded-2xl overflow-hidden border border-gray-100" style={{ height: 380 }}>
                        {selectedProduct.images?.length > 0
                          ? <img src={selectedProduct.images[selectedImageIdx]} alt={selectedProduct.name} className="w-full h-full object-contain p-4" />
                          : <div className="w-full h-full flex items-center justify-center"><TagIcon className="h-20 w-20 text-gray-200" /></div>}
                        {isOfferActive(selectedProduct.offer) && (
                          <div className="absolute top-3 left-3">
                            <span className="text-xs font-extrabold text-white px-2.5 py-1 rounded-lg shadow-md" style={{ background: '#dc2626' }}>SALE</span>
                          </div>
                        )}
                        <button onClick={() => toggleLike(selectedProduct)}
                          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white shadow-md flex items-center justify-center transition-transform hover:scale-110">
                          {likedProducts.includes(selectedProduct.id)
                            ? <HeartSolidIcon className="h-5 w-5" style={{ color: '#ef4444' }} />
                            : <HeartIcon className="h-5 w-5 text-gray-400" />}
                        </button>
                      </div>
                      {selectedProduct.images?.length > 1 && (
                        <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
                          {selectedProduct.images.map((img, i) => (
                            <button key={i} onClick={() => setSelectedImageIdx(i)}
                              className="w-16 h-16 flex-shrink-0 rounded-xl overflow-hidden border-2 transition-all"
                              style={{ borderColor: selectedImageIdx === i ? '#800020' : '#e5e7eb' }}>
                              <img src={img} alt="" className="w-full h-full object-cover" />
                            </button>
                          ))}
                        </div>
                      )}
                      {selectedProduct.totalLikes > 0 && (
                        <div className="flex items-center gap-1.5 mt-3 px-1">
                          <HeartSolidIcon className="h-4 w-4" style={{ color: '#ef4444' }} />
                          <span className="text-sm text-gray-500">{selectedProduct.totalLikes} {selectedProduct.totalLikes === 1 ? 'person likes' : 'people like'} this</span>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-5">
                      <div>
                        <h3 className="text-2xl font-extrabold text-gray-900 leading-tight">{selectedProduct.name}</h3>
                        {selectedProduct.brand && <p className="text-sm text-gray-500 mt-1">{selectedProduct.brand}</p>}
                        {selectedProduct.averageRating > 0 && (
                          <div className="flex items-center gap-2 mt-2">
                            <Stars rating={selectedProduct.averageRating} size="md" />
                            <span className="text-sm text-gray-500">{selectedProduct.averageRating.toFixed(1)} ({selectedProduct.totalRatings} rating{selectedProduct.totalRatings !== 1 ? 's' : ''})</span>
                          </div>
                        )}
                      </div>
                      <div className="p-4 rounded-2xl" style={{ background: '#fef2f2' }}>
                        <PriceDisplay product={selectedProduct} isMember={isMember} size="lg" />
                        {(() => {
                          const { isOffer, isMemberDeal } = resolvePrice(selectedProduct, isMember);
                          if (isOffer) return <p className="text-xs text-red-600 mt-1.5 font-medium">🔥 Limited time offer{selectedProduct.offer?.endDate ? ` — ends ${new Date(selectedProduct.offer.endDate).toLocaleDateString('en-KE', { day: 'numeric', month: 'short' })}` : ''}</p>;
                          if (isMemberDeal) return <p className="text-xs mt-1.5 font-medium" style={{ color: '#800020' }}>⚡ Your exclusive member price</p>;
                          return null;
                        })()}
                      </div>
                      {selectedProduct.description && <p className="text-gray-600 text-sm leading-relaxed">{selectedProduct.description}</p>}
                      {selectedProduct.features?.length > 0 && (
                        <div>
                          <p className="text-sm font-bold text-gray-800 mb-2">Key Features</p>
                          <ul className="space-y-1.5">
                            {selectedProduct.features.map((f, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                                <SparklesIcon className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: '#800020' }} />{f}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <div className="flex gap-3">
                        <button
                          onClick={() => { if (!cart.some(i => i.id === selectedProduct.id)) addToCart(selectedProduct); }}
                          disabled={cart.some(i => i.id === selectedProduct.id)}
                          className="flex-1 py-3 rounded-2xl font-bold text-sm text-white shadow transition-all flex items-center justify-center gap-2"
                          style={{ background: cart.some(i => i.id === selectedProduct.id) ? '#16a34a' : 'linear-gradient(135deg,#800020,#b30000)', cursor: cart.some(i => i.id === selectedProduct.id) ? 'not-allowed' : 'pointer', opacity: cart.some(i => i.id === selectedProduct.id) ? 0.9 : 1 }}
                          onMouseEnter={e => { if (!cart.some(i => i.id === selectedProduct.id)) { e.currentTarget.style.opacity = '0.88'; e.currentTarget.style.transform = 'scale(1.02)'; } }}
                          onMouseLeave={e => { e.currentTarget.style.opacity = cart.some(i => i.id === selectedProduct.id) ? '0.9' : '1'; e.currentTarget.style.transform = 'scale(1)'; }}>
                          {cart.some(i => i.id === selectedProduct.id) ? <><CheckCircleIcon className="h-4 w-4" /> In Cart</> : 'Add to Cart'}
                        </button>
                        <button onClick={() => openWhatsApp(selectedProduct)}
                          className="flex items-center gap-2 px-5 py-3 rounded-2xl font-bold text-sm text-white shadow transition-all"
                          style={{ background: '#25D366' }}
                          onMouseEnter={e => { e.currentTarget.style.background = '#1da851'; e.currentTarget.style.transform = 'scale(1.02)'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = '#25D366'; e.currentTarget.style.transform = 'scale(1)'; }}>
                          <WhatsAppIcon className="h-4 w-4" /> Order
                        </button>
                      </div>
                      <div className="border-t border-gray-100 pt-5">
                        <p className="text-sm font-bold text-gray-800 mb-3">Rate this product</p>
                        <div className="flex items-center gap-2 mb-3">
                          {[1,2,3,4,5].map(star => (
                            <button key={star} onMouseEnter={() => setHoverRating(star)} onMouseLeave={() => setHoverRating(0)} onClick={() => setUserRating(star)} className="focus:outline-none transition-transform hover:scale-110">
                              {(hoverRating || userRating) >= star ? <StarSolidIcon className="h-8 w-8 text-yellow-400" /> : <StarIcon className="h-8 w-8 text-gray-200" />}
                            </button>
                          ))}
                          {userRating > 0 && <span className="text-sm text-gray-500 ml-1">{['','Poor','Fair','Good','Very Good','Excellent'][userRating]}</span>}
                        </div>
                        <textarea value={ratingComment} onChange={e => setRatingComment(e.target.value)}
                          placeholder="Share your experience (optional)…" rows={3}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none transition-all mb-3"
                          style={{ borderColor: ratingComment ? '#800020' : '#e5e7eb' }} />
                        <button onClick={submitRating}
                          className="w-full py-2.5 rounded-xl text-sm font-bold text-white"
                          style={{ background: 'linear-gradient(135deg,#800020,#b30000)' }}
                          onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
                          onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                          Submit Rating
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        .line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
        .swal-wa-popup { border-radius: 24px !important; }
      `}</style>
    </div>
  );
};

export default Shop;